import React from "react";
import { Box, Container, IconButton, styled, Typography } from "@mui/material";
import Slider from "react-slick";
import AuthorCard from "../../../components/cards/AuthorCard";
import { SectionTitle } from "../../../components/home-section-title/HomeSectionTitle";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import usePopularAuthors from "../../../hooks/api-request/usePopularAuthors";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import LazyLoad from "react-lazyload";
import { useTranslation } from "react-i18next";

const SliderArrow = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 1,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
  },
  "&.slick-disabled": {
    display: "none",
  },
}));

const SliderWrapper = styled(Box)(({ theme }) => ({
  "& .slick-list": {
    margin: 0,
    overflow: "visible",
    [theme.breakpoints.down("md")]: {
      overflow: "hidden",
    },
  },
  "& .slick-track": {
    display: "flex",
    alignItems: "stretch",
    gap: theme.spacing(4),
  },
  "& .slick-slide": {
    height: "auto",
    "& > div": {
      height: "100%",
    },
  },
}));

const AuthorSlide = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(4),
  padding: theme.spacing(2),
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}));

const AuthorSlidesSkeleton = () => (
  <Box sx={{ position: "relative" }}>
    <AuthorSlide>
      <Box
        sx={{
          flex: 1,
          opacity: 0.8,
          transition: "opacity 0.3s ease",
        }}
      >
        <AuthorCard loading />
      </Box>
      <Box
        sx={{
          flex: 1,
          opacity: 0.6,
          transition: "opacity 0.3s ease",
        }}
      >
        <AuthorCard loading />
      </Box>
    </AuthorSlide>
  </Box>
);

const PopularAuthors = () => {
  const sliderRef = React.useRef(null);
  const [currentSlide, setCurrentSlide] = React.useState(1);
  const { authors, loading, error } = usePopularAuthors();
  const { t, i18n } = useTranslation();
  
  // Check if the current language is RTL
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';
  
  // Handle arrow icons and directions based on language
  const NextArrow = isRtl ? ArrowBackIosNewIcon : ArrowForwardIosIcon;
  const PrevArrow = isRtl ? ArrowForwardIosIcon : ArrowBackIosNewIcon;

  const totalSlides = Math.ceil((authors?.length || 0) / 2);

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    beforeChange: (_, next) => setCurrentSlide(next + 1),
    lazyLoad: true,
    adaptiveHeight: true,
    rtl: isRtl, // Set RTL for slider based on language
  };

  const renderContent = () => {
    if (loading) {
      return [1, 2].map((_, index) => <AuthorSlidesSkeleton key={index} />);
    }

    if (error) {
      return (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography color="error">{t('home.errors.failedToLoad')}</Typography>
        </Box>
      );
    }

    if (!authors?.length) {
      return (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            {t('home.popularAuthors.noAuthors')}
          </Typography>
        </Box>
      );
    }

    return authors.reduce((acc, _, index) => {
      if (index % 2 === 0) {
        acc.push(
          <AuthorSlide key={index}>
            <Box sx={{ flex: 1 }}>
              <AuthorCard author={authors[index]} />
            </Box>
            {authors[index + 1] && (
              <Box sx={{ flex: 1 }}>
                <AuthorCard author={authors[index + 1]} />
              </Box>
            )}
          </AuthorSlide>,
        );
      }
      return acc;
    }, []);
  };

  return (
    <Box
      sx={{ py: { xs: 3, md: 6 }, position: "relative", overflow: "hidden" }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            position: "relative",
            mb: { xs: 2, md: 4 },
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <SectionTitle variant="h2">
            {t('home.popularAuthors.title')} {!loading && `(${authors?.length || 0})`}
          </SectionTitle>
          {!loading && authors?.length > 0 && (
            <Box
              sx={{
                display: { xs: "none", md: "block" },
                color: "text.secondary",
                typography: "body2",
              }}
            >
              {currentSlide} {t('home.of')} {totalSlides}
            </Box>
          )}
        </Box>

        <SliderWrapper>
          <Box sx={{ position: "relative" }}>
            {loading ? (
              // Show skeleton directly when loading
              <Box>
                <AuthorSlidesSkeleton />
                <AuthorSlidesSkeleton />
              </Box>
            ) : (
              <LazyLoad
                height={400}
                offset={200}
                once
                placeholder={<AuthorSlidesSkeleton />}
              >
                <Slider ref={sliderRef} {...settings}>
                  {renderContent()}
                </Slider>
              </LazyLoad>
            )}

            {!loading && authors?.length > 2 && (
              <>
                <SliderArrow
                  onClick={() => sliderRef.current?.slickPrev()}
                  sx={{
                    left: { xs: 0, md: -20 },
                    display: { xs: "none", md: "flex" },
                  }}
                >
                  <PrevArrow />
                </SliderArrow>

                <SliderArrow
                  onClick={() => sliderRef.current?.slickNext()}
                  sx={{
                    right: { xs: 0, md: -20 },
                    display: { xs: "none", md: "flex" },
                  }}
                >
                  <NextArrow />
                </SliderArrow>
              </>
            )}
          </Box>
        </SliderWrapper>
      </Container>
    </Box>
  );
};

export default React.memo(PopularAuthors);
